import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { APP_PATHS } from '../../auth/role-based-access-control';

const blobBounce = keyframes`
  0%   { transform: translate(-100%, -100%) translate3d(0, 0, 0); }
  25%  { transform: translate(-100%, -100%) translate3d(100%, 0, 0); }
  50%  { transform: translate(-100%, -100%) translate3d(100%, 100%, 0); }
  75%  { transform: translate(-100%, -100%) translate3d(0, 100%, 0); }
  100% { transform: translate(-100%, -100%) translate3d(0, 0, 0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-12px); }
`;

export default function AccessDeniedPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isDark
            ? alpha(theme.palette.primary.main, 0.18)
            : alpha(theme.palette.divider, 0.8),
          boxShadow: isDark
            ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}, 0 8px 40px ${alpha(theme.palette.common.black, 0.45)}`
            : `0 4px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        {/* Animated blob glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            opacity: isDark ? 0.08 : 0.12,
            filter: 'blur(56px)',
            animation: `${blobBounce} 10s infinite ease-in-out`,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            p: { xs: 3, sm: 4 },
          }}
        >
          <Stack spacing={3} alignItems="center">
            {/* Animated lock icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, isDark ? 0.55 : 0.35)}`,
                animation: `${float} 3s ease-in-out infinite`,
              }}
            >
              <LockPersonIcon sx={{ fontSize: 44, color: 'primary.contrastText' }} />
            </Box>

            {/* Heading */}
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                component="h1"
                variant="h4"
                fontWeight={700}
                color="primary.main"
                sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}
              >
                Access Denied
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 320,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  lineHeight: 1.6,
                }}
              >
                Access denied with the current role. Kindly contact support.
              </Typography>
            </Stack>

            {/* Info box */}
            <Box
              sx={{
                width: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: isDark
                  ? alpha(theme.palette.warning.main, 0.35)
                  : alpha(theme.palette.warning.main, 0.4),
                bgcolor: isDark
                  ? alpha(theme.palette.warning.main, 0.06)
                  : alpha(theme.palette.warning.main, 0.07),
                p: 2,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <WarningAmberRoundedIcon
                  sx={{
                    fontSize: 20,
                    color: 'warning.main',
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.8125rem', lineHeight: 1.6 }}
                >
                  You can view only the pages your account role is allowed to
                  access. If you believe this is an error, please contact your
                  administrator.
                </Typography>
              </Stack>
            </Box>

            {/* CTA */}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<HomeRoundedIcon />}
              onClick={() => navigate(APP_PATHS.DASHBOARD)}
              sx={{
                py: 1.25,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 600,
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, isDark ? 0.45 : 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, isDark ? 0.6 : 0.4)}`,
                },
              }}
            >
              Go to Dashboard
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
